import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { HiOutlineSearch } from "react-icons/hi";
import UserFilters from "../components/userFilters";

import CreateDataBaseModal from "../../../components/models/createDatabaseModal";
import AlertNotification from "../../../components/notification";
import { PaginationComponent } from "../../../components/ui";

import UserCard from "../components/userCard";
import {
  fetchFilteredUsers,
  fetchHeadAccounts,
  exportDatabase,
} from "../../../controllers/databaseController";
import { RECORD_CATEGORIES } from "../../../constants";

function DataBaseMangement() {
  // Modals & notifications
  const [showAddDatabaseModal, setShowAddDatabaseModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [status, setStatus] = useState(false);

  // Data
  const [users, setUsers] = useState([]);
  const [headAccounts, setHeadAccounts] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [recordsPerPage] = useState(100); // Fixed at 100 records per page

  // Filter inputs
  const [locationInput, setLocationInput] = useState("");
  const [jobTitleInput, setJobTitleInput] = useState("");
  const [industryInput, setIndustryInput] = useState("");
  const [linkedinInput, setLinkedinInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [companyNameInput, setCompanyNameInput] = useState("");

  const [appliedFilters, setAppliedFilters] = useState({
    location: [],
    jobTitle: [],
    email: [],
    industry: [],
    headAccount: [],
    linkedin: [],
    companyName: [],
    category: RECORD_CATEGORIES.WARM,
  });

  // FTE dropdown open state (was missing)
  const [fteDropdownOpen, setFteDropdownOpen] = useState(false);

  const fteDropdownRef = useRef(null);
  const resultsScrollRef = useRef(null);

  // Start each page at the top of the scrollable card area
  useEffect(() => {
    resultsScrollRef.current?.scrollTo({ top: 0 });
  }, [currentPage, appliedFilters]);

  // Disable right-click & copy globally
  useEffect(() => {
    const disableRightClick = (e) => e.preventDefault();
    const disableCopy = (e) => e.preventDefault();

    document.addEventListener("contextmenu", disableRightClick);
    document.addEventListener("copy", disableCopy);

    return () => {
      document.removeEventListener("contextmenu", disableRightClick);
      document.removeEventListener("copy", disableCopy);
    };
  }, []);

  // Fetch head-account options
  useEffect(() => {
    fetchHeadAccounts().then((data) => {
      setHeadAccounts(data);
    });
  }, []);

  // Fetch filtered data whenever filters or page change
  useEffect(() => {
    // Ignore responses from superseded requests, so an older response can't
    // replace newer data (or end the loading state) after the fact.
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await fetchFilteredUsers({
          filters: appliedFilters,
          page: currentPage,
          limit: recordsPerPage,
        });
        if (cancelled) return;

        const enriched = data.data.map((item) => ({
          id: item.id,
          name: item.name || "",
          email: item.email || "",
          email2: item.email2 || "",
          jobTitle: item.jobTitle || "",
          location: item.location || "",
          linkedIn: item.linkedIn || "",
          industry: item.industry || "",
          headAccount: item.headAccount || "",
          headAccountId: item.headAccountId || "",
          companyName: item.companyName || "",
        }));

        setUsers(enriched);
        setTotalPages(data.totalPages || 0);
        setTotalRecords(data.total || 0);
        setCategoryCounts((prev) => ({
          ...prev,
          [appliedFilters.category]: data.total || 0,
        }));
      } catch (err) {
        if (!cancelled) console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [appliedFilters, currentPage, recordsPerPage]);

  // Count for the non-selected category, so the Warm/Cold toggle shows both
  useEffect(() => {
    const otherCategory =
      appliedFilters.category === RECORD_CATEGORIES.WARM
        ? RECORD_CATEGORIES.COLD
        : RECORD_CATEGORIES.WARM;
    let cancelled = false;

    fetchFilteredUsers({
      filters: { ...appliedFilters, category: otherCategory },
      page: 1,
      limit: 1,
    })
      .then((data) => {
        if (cancelled) return;
        setCategoryCounts((prev) => ({
          ...prev,
          [otherCategory]: data.total || 0,
        }));
      })
      .catch((err) => console.error(err));

    return () => {
      cancelled = true;
    };
  }, [appliedFilters]);

  // Generic tag add helper
  const onEnterAddTag = (value, stateKey) => {
    if (!value.trim()) return;
    setAppliedFilters((prev) => {
      const arr = prev[stateKey];
      if (arr.includes(value)) return prev;
      return { ...prev, [stateKey]: [...arr, value] };
    });
  };

  // Remove a tag
  const removeTag = (stateKey, val) => {
    setAppliedFilters((prev) => ({
      ...prev,
      [stateKey]: prev[stateKey].filter((x) => x !== val),
    }));
  };

  // Toggle FTE selection
  const toggleFTE = (id) =>
    setAppliedFilters((prev) => {
      const arr = prev.headAccount;
      return {
        ...prev,
        headAccount: arr.includes(id)
          ? arr.filter((x) => x !== id)
          : [...arr, id],
      };
    });

  // Clear all filters
  const cancelFilters = () => {
    setAppliedFilters((prev) => ({
      location: [],
      jobTitle: [],
      email: [],
      industry: [],
      headAccount: [],
      linkedin: [],
      companyName: [],
      category: prev.category,
    }));
    setCurrentPage(1);
  };

  // Switch between Warm and Cold record categories
  const setCategory = (category) => {
    setAppliedFilters((prev) => ({ ...prev, category }));
  };

  // Reset to page 1 when filters change (but not on initial load)
  const prevFiltersRef = useRef();

  useEffect(() => {
    const hasFiltersChanged =
      prevFiltersRef.current &&
      JSON.stringify(prevFiltersRef.current) !== JSON.stringify(appliedFilters);

    if (hasFiltersChanged && currentPage !== 1) {
      setCurrentPage(1);
    }

    prevFiltersRef.current = appliedFilters;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  // Handle export
  const handleExportClick = async () => {
    setIsDownloading(true);
    try {
      const resp = await exportDatabase(appliedFilters);

      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `email_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setStatus(true);
      setNotification("Exported successfully");
    } catch {
      setStatus(false);
      setNotification("Export failed");
    } finally {
      setTimeout(() => setIsDownloading(false), 500);
    }
  };

  const firstRecord = totalRecords ? (currentPage - 1) * recordsPerPage + 1 : 0;
  const lastRecord = Math.min(currentPage * recordsPerPage, totalRecords);

  return (
    <div className="flex w-full max-w-full flex-col no-select lg:h-[calc(100vh-112px)] lg:overflow-hidden">
      {/* Page description */}
      <div className="mb-6 flex shrink-0 flex-col gap-1">
        <p className="text-sm text-app-muted">
          Search and filter contact records, then export the ones you need.
        </p>

        {/* T14: Add Record temporarily disabled
        <div className="mr-2 inline-flex gap-2">
          <ButtonComponent
            onClick={() => setShowAddDatabaseModal(true)}
            className="bg-[#2470B7] hover:bg-[#2470B7]"
          >
            + Add Record
          </ButtonComponent>
        </div>
        */}
      </div>

      <div className="grid w-full grid-cols-1 items-start gap-6 lg:min-h-0 lg:flex-1 lg:grid-cols-[300px_minmax(0,1fr)]">
        {/* Filter SIDEBAR */}
        <motion.aside
          initial={{ x: -16, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.25 }}
          aria-label="Filters"
          className="overflow-hidden rounded-xl border border-app-primary bg-app-card shadow-sm lg:flex lg:max-h-full lg:flex-col"
        >
          <UserFilters
            draftFilters={appliedFilters}
            linkedinInput={linkedinInput}
            companyNameInput={companyNameInput}
            locationInput={locationInput}
            jobTitleInput={jobTitleInput}
            industryInput={industryInput}
            emailInput={emailInput}
            setEmailInput={setEmailInput}
            setLinkedinInput={setLinkedinInput}
            setCompanyNameInput={setCompanyNameInput}
            setLocationInput={setLocationInput}
            setJobTitleInput={setJobTitleInput}
            setIndustryInput={setIndustryInput}
            headAccounts={headAccounts}
            fteDropdownRef={fteDropdownRef}
            fteDropdownOpen={fteDropdownOpen}
            setFteDropdownOpen={setFteDropdownOpen}
            toggleFTE={toggleFTE}
            onEnterAddTag={onEnterAddTag}
            removeTag={removeTag}
            cancelFilters={cancelFilters}
            handleExportClick={handleExportClick}
            isDownloading={isDownloading}
            appliedFilters={appliedFilters}
            category={appliedFilters.category}
            categoryCounts={categoryCounts}
            setCategory={setCategory}
          />
        </motion.aside>

        {/* RESULTS */}
        <section
          aria-label="Results"
          className="flex min-w-0 flex-col gap-4 lg:h-full lg:min-h-0"
        >
          {/* Results toolbar */}
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-xl border border-app-primary bg-app-card px-4 py-3 shadow-sm">
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-app-muted" aria-live="polite">
                {loading ? (
                  "Loading records…"
                ) : (
                  <>
                    Showing{" "}
                    <span className="font-semibold text-app-heading">
                      {firstRecord.toLocaleString()}–
                      {lastRecord.toLocaleString()}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-app-heading">
                      {totalRecords.toLocaleString()}
                    </span>{" "}
                    records
                  </>
                )}
              </p>

              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                  loading
                    ? "bg-app-secondary text-app-muted ring-app-primary"
                    : "bg-green-50 text-green-700 ring-green-200 dark:bg-green-500/10 dark:text-green-400 dark:ring-green-500/20"
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    loading ? "animate-pulse bg-gray-400" : "bg-green-500"
                  }`}
                />
                {loading ? "Updating" : "Up to date"}
              </span>
            </div>

            <PaginationComponent
              variant="compact"
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              recordsPerPage={recordsPerPage}
              onPageChange={setCurrentPage}
              loading={loading}
            />
          </div>

          {/* Scrollable card area: only this region scrolls */}
          <div
            ref={resultsScrollRef}
            className="-m-1 max-h-[70vh] min-h-0 overflow-y-auto p-1 lg:max-h-none lg:flex-1"
          >
            {loading ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {Array.from({ length: 6 }, (_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-app-primary bg-app-card p-4"
                  >
                    <div className="flex gap-3">
                      <div className="h-10 w-10 rounded-full bg-app-secondary" />
                      <div className="flex-1 space-y-2 py-1">
                        <div className="h-3.5 w-2/5 rounded bg-app-secondary" />
                        <div className="h-3 w-3/5 rounded bg-app-secondary" />
                        <div className="h-3 w-1/3 rounded bg-app-secondary" />
                      </div>
                    </div>
                    <div className="mt-4 space-y-2 border-t border-app-secondary pt-3">
                      <div className="h-3 w-1/2 rounded bg-app-secondary" />
                      <div className="h-3 w-2/3 rounded bg-app-secondary" />
                    </div>
                  </div>
                ))}
              </div>
            ) : users.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col items-center justify-center rounded-xl border border-dashed border-app-primary bg-app-card px-6 py-16 text-center"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#2470B7]/10 text-[#2470B7]">
                  <HiOutlineSearch className="h-6 w-6" />
                </div>
                <h2 className="text-base font-semibold text-app-heading">
                  No records found
                </h2>
                <p className="mt-1 max-w-sm text-sm text-app-muted">
                  Try removing a filter or searching with different keywords.
                </p>
              </motion.div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                  {users.map((user, i) => (
                    <UserCard key={user.id || i} user={user} />
                  ))}
                </div>

                <PaginationComponent
                  className="pt-6"
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalRecords={totalRecords}
                  recordsPerPage={recordsPerPage}
                  onPageChange={setCurrentPage}
                  loading={loading}
                />
              </>
            )}
          </div>
        </section>
      </div>

      {/* Modals */}
      <CreateDataBaseModal
        show={showAddDatabaseModal}
        handleClose={() => setShowAddDatabaseModal(false)}
        setNotification={setNotification}
        setStatus={setStatus}
        fetchData={() => setAppliedFilters((f) => ({ ...f }))}
      />

      {notification && (
        <AlertNotification
          text={notification}
          status={status}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
}

export default DataBaseMangement;
